import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  AlertComponent,
  ButtonComponent,
  DropdownFieldComponent,
  DropdownOption,
  FormSectionComponent,
  SkeletonComponent,
  TextFieldComponent,
} from 'htx-ui-library';

import { OrganizationService } from '../../../services/organizations/organization.service';
import { NotificationService } from '../../../services/form/notification.service';
import { buildSalutationOptions } from '../../../services/form/salutation-options';

@Component({
  selector: 'app-profile-page',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    TextFieldComponent,
    DropdownFieldComponent,
    FormSectionComponent,
    AlertComponent,
    ButtonComponent,
    SkeletonComponent,
  ],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.scss'
})
export class ProfilePageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.organizationService.isOrganizationPending$().subscribe(pending => {
      this.isOrganizationPending.set(pending);
    });
  }

  readonly salutationOptions = signal<DropdownOption[]>(buildSalutationOptions(this.translocoService));
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly loadError = signal(false);
  readonly isOrganizationPending = signal(false);
  readonly isDsaUser = signal(false);

  private memberId: string | null = null;
  private roles: string[] | null = null;
  private originalEmail = '';

  form = this.fb.group({
    title: [''],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    position: [''],
    phoneNumber: [''],
  });

  ngOnInit(): void {
    this.translocoService.selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.salutationOptions.set(buildSalutationOptions(this.translocoService));
      });
    this.loadProfile();
  }

  private loadProfile(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.organizationService.getMyMemberProfile().subscribe({
      next: (member) => {
        this.memberId = member.id;
        this.originalEmail = member.email ?? '';
        this.form.patchValue({
          title: member.title ?? '',
          firstName: member.firstName,
          lastName: member.lastName,
          email: member.email,
          position: member.position ?? '',
          phoneNumber: member.phoneNumber ?? '',
        });
        this.roles = member.roles ?? [];
        const isDsa = this.roles.some(r => r.toUpperCase() === 'DATASPACE_AUTHORITY');
        this.isDsaUser.set(isDsa);
        if (isDsa) {
          this.form.controls.title.disable();
          this.form.controls.position.disable();
          this.form.controls.phoneNumber.disable();
        } else {
          this.form.controls.title.enable();
          this.form.controls.position.enable();
          this.form.controls.phoneNumber.enable();
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.loadError.set(true);
        this.isLoading.set(false);
      }
    });
  }

  save(): void {
    if (this.form.invalid || this.isSaving()) return;

    this.isSaving.set(true);
    const raw = this.form.getRawValue();
    const newEmail = raw.email ?? '';
    const emailChanged = newEmail !== this.originalEmail;

    this.organizationService.updateOwnMember({
      title: raw.title ?? undefined,
      firstName: raw.firstName ?? '',
      lastName: raw.lastName ?? '',
      position: raw.position ?? undefined,
      phoneNumber: raw.phoneNumber ?? undefined,
      email: newEmail,
      roles: this.roles ?? [], // Roles cannot be updated by the user themselves
    }).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.notificationService.showSuccess(
          this.translocoService.translate(emailChanged ? 'profile.emailChangeNotice' : 'profile.saveSuccess')
        );
        if (emailChanged) {
          // Reload so the baseline email and the displayed value reflect the saved change.
          this.loadProfile();
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.notificationService.showError(
          this.translocoService.translate('profile.saveError')
        );
      }
    });
  }
}
